// components/ui/PageEmoji.tsx
//
// A page's emoji, the way Notion does it: the character itself, nothing behind it.
//
// This replaces GlassGlyph, which framed the emoji in a frosted chip and, when none was chosen,
// drew a monochrome line icon on that chip instead. Both were wrong for the same reason: an emoji
// is already a picture with its own shape and colour, so a container gives it a second competing
// edge and makes a page icon read as a button.
//
// There is always a glyph. A page with no chosen emoji gets a sensible default rather than an
// empty space, so the title never sits alone against the margin and the double-click target is
// always there to find.
'use client';

import React from 'react';

export const PageEmoji = ({
  emoji,
  fallback,
  size = 30,
  title,
  onDoubleClick,
}: {
  /** The chosen emoji, if the reader has picked one. */
  emoji?: string;
  /** What to show when they have not. */
  fallback: string;
  /** Font size in px. */
  size?: number;
  title?: string;
  onDoubleClick?: () => void;
}) => (
  <span
    // Not aria-hidden when it is interactive: a control the keyboard and a screen reader cannot
    // find is not a control.
    aria-hidden={onDoubleClick ? undefined : true}
    role={onDoubleClick ? 'button' : undefined}
    tabIndex={onDoubleClick ? 0 : undefined}
    title={title}
    onDoubleClick={onDoubleClick}
    onKeyDown={onDoubleClick ? (event) => {
      if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onDoubleClick(); }
    } : undefined}
    className={`shrink-0 select-none leading-none${onDoubleClick ? ' cursor-pointer transition-transform hover:scale-[1.08]' : ''}`}
    style={{ fontSize: size, lineHeight: 1 }}
  >
    {emoji || fallback}
  </span>
);
